import React from 'react';
import { Container, Typography, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const WatchHistory: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/help')}
          sx={{ mb: 4, color: 'text.secondary' }}
        >
          {t('help.backToHelp', 'Back to Help Center')}
        </Button>

        <Typography variant="overline" color="primary" sx={{ fontWeight: 600, letterSpacing: 2, display: 'block', mb: 2 }}>
          For Viewers
        </Typography>

        <Typography variant="h3" component="h1" sx={{ mb: 3, fontWeight: 700 }}>
          Managing Your Watch History
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85, fontSize: '1.1rem' }}>
          IndeeClips tracks videos you've watched to improve recommendations and let you revisit content. Learn how to view, delete, and pause your watch history.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Viewing Watch History
        </Typography>
        <Box component="ol" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Click your profile icon</li>
          <li>Select "History" or "Watch History"</li>
          <li>See chronological list of videos you've watched</li>
          <li>Scroll to browse older history</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Searching Your History
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Use search bar at top of History page</li>
          <li>Type keywords to find specific videos</li>
          <li>Filter by date range (Today, This Week, This Month, Custom)</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Deleting Individual Videos from History
        </Typography>
        <Box component="ol" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Find the video in your watch history</li>
          <li>Click three-dot menu next to video</li>
          <li>Select "Remove from Watch History"</li>
          <li>Video disappears from your history immediately</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Clearing All Watch History
        </Typography>
        <Box component="ol" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Go to Settings → Privacy</li>
          <li>Find "Watch History" section</li>
          <li>Click "Clear All Watch History"</li>
          <li>Confirm action</li>
          <li>All history deleted permanently</li>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          <strong>Note:</strong> Clearing history also resets video recommendations—the algorithm starts fresh.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Pausing Watch History
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Temporarily stop IndeeClips from saving what you watch:
        </Typography>
        <Box component="ol" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Settings → Privacy → Watch History</li>
          <li>Toggle "Pause Watch History"</li>
          <li>Videos watched while paused won't be saved</li>
          <li>Toggle off to resume tracking</li>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          <strong>Use case:</strong> Watching content you don't want to influence recommendations (e.g., watching kids' videos on your account).
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          How Watch History Affects Recommendations
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Algorithm learns your preferences from what you watch</li>
          <li>Videos you watch fully get more weight than those you skip</li>
          <li>Recent history matters more than old history</li>
          <li>Deleting videos removes their influence on recommendations</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Privacy Settings
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Control who can see your watch history:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Private (default):</strong> Only you can see your watch history</li>
          <li><strong>Public:</strong> Other users can view what you've watched (not recommended)</li>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          Change in Settings → Privacy → Watch History Visibility.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Auto-Delete History
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Automatically delete watch history older than a set period:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Settings → Privacy → Auto-Delete Watch History</li>
          <li>Choose: 3 months, 6 months, 12 months, or Never</li>
          <li>History older than your selection gets deleted automatically</li>
        </Box>

        <Box
          sx={{
            mt: 6,
            p: 4,
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
            Questions About History?
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            Contact support at{' '}
            <Typography
              component="a"
              href="mailto:contact@indeeclips.com"
              sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              contact@indeeclips.com
            </Typography>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default WatchHistory;
